/**
 * Formats a UTC ISO-8601 timestamp into a human-readable date string
 * using react-intl's formatMessage with {day} and {month} placeholders.
 *
 * Example: "2025-05-19T02:30:00Z" → "Generated 19th May" (en, via selectordinal)
 *                                 → "Généré le 19 mai" (fr, via translation)
 *
 * Date values are derived from the user's local timezone so the displayed date
 * matches what the user sees on their own clock.
 */
import type { IntlShape, MessageDescriptor } from 'react-intl';

export type FormatMessageFn = IntlShape['formatMessage'];

/**
 * Converts a UTC ISO-8601 timestamp to a localised date string like "Generated 19 May".
 *
 * Requires a `MessageDescriptor` with `{day}` (number) and `{month}` (string) placeholders:
 *   e.g. `'Generated {day} {month}'`
 *
 * Both values are derived from the user's local timezone so the date matches their clock.
 *
 * Returns `undefined` if the input is falsy or cannot be parsed.
 */
export function formatGeneratedDate(
	isoTimestamp: string | undefined | null,
	formatMessage: FormatMessageFn,
	message: MessageDescriptor,
	locale?: string,
	timeZone?: string,
): string | undefined {
	if (!isoTimestamp) {
		return undefined;
	}

	const date = new Date(isoTimestamp);
	if (isNaN(date.getTime())) {
		return undefined;
	}

	// Use the user's local timezone by default so the date matches what they see on their clock.
	const dateTimeFormatOptions: Intl.DateTimeFormatOptions = {
		day: 'numeric',
		month: 'long',
		...(timeZone ? { timeZone } : {}),
	};
	const parts = new Intl.DateTimeFormat(locale ?? undefined, dateTimeFormatOptions).formatToParts(
		date,
	);
	const day = Number(parts.find((part) => part.type === 'day')?.value);
	const month = parts.find((part) => part.type === 'month')?.value;

	return formatMessage(message, { day, month });
}
